import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const AILoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: linear-gradient(135deg, rgba(232, 19, 19, 0.05), rgba(255, 77, 77, 0.02));
  border-radius: 20px;
  border: 1px solid rgba(232, 19, 19, 0.2);
  margin: 1rem 0;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(232, 19, 19, 0.1) 0%, transparent 70%);
    animation: pulseGlow 3s ease-in-out infinite;
  }
  
  @keyframes pulseGlow {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.7; }
  }
`;

const BrainContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 2rem;
  z-index: 2;
`;

const BrainCore = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e81313, #ff4d4d);
  border-radius: 50%;
  position: relative;
  box-shadow: 0 0 40px rgba(232, 19, 19, 0.4);
  animation: brainPulse 2s ease-in-out infinite;
  
  @keyframes brainPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;

const BrainInner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70%;
  height: 70%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
  border-radius: 50%;
  backdrop-filter: blur(5px);
`;

const NeuralNetwork = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200%;
  height: 200%;
  pointer-events: none;
`;

const NeuralNode = styled.div<{ $delay: number; $duration: number; $distance: number }>`
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(232, 19, 19, 0.8);
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform-origin: center;
  animation: neuralOrbit ${props => props.$duration}s linear infinite;
  animation-delay: ${props => props.$delay}s;
  
  @keyframes neuralOrbit {
    0% {
      transform: rotate(0deg) translateX(${props => props.$distance}px) rotate(0deg);
      opacity: 0;
    }
    10% {
      opacity: 1;
    }
    90% {
      opacity: 1;
    }
    100% {
      transform: rotate(360deg) translateX(${props => props.$distance}px) rotate(-360deg);
      opacity: 0;
    }
  }
`;

const LoadingText = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 1rem;
  min-height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  position: relative;
`;

const LoadingTextWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  position: relative;
  white-space: nowrap;
`;

const LoadingTextContent = styled.span`
  display: inline-block;
  background: linear-gradient(135deg, #e81313, #ff4d4d);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`;

const LoadingDots = styled.span`
  display: inline-block;
  min-width: 2rem;
  text-align: left;
  color: #e81313;
  font-weight: 700;
  
  @keyframes thinkingDots {
    0%, 20% { opacity: 0; }
    50% { opacity: 1; }
    100% { opacity: 0; }
  }
  
  animation: thinkingDots 1.5s infinite;
`;

const Subtitle = styled.div`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  opacity: 0.8;
  margin-bottom: 1.5rem;
  z-index: 2;
  position: relative;
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  z-index: 2;
  position: relative;
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #e81313, #ff4d4d);
  border-radius: 2px;
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
  box-shadow: 0 0 10px rgba(232, 19, 19, 0.5);
`;

interface AILoadingScreenProps {
  texts?: string[];
  subtitle?: string;
  showProgress?: boolean;
}

export const AILoadingScreen: React.FC<AILoadingScreenProps> = ({ 
  texts = [
    'AI is analyzing patterns',
    'Processing team statistics',
    'Generating intelligent insights',
    'Calculating optimal strategies',
    'Neural networks are thinking'
  ],
  subtitle = 'Our AI is working hard to provide you with the best insights',
  showProgress = true
}) => {
  const [currentText, setCurrentText] = useState(texts[0]);
  const [dotCount, setDotCount] = useState(0);
  const [progress, setProgress] = useState(0);
  
  // Animate dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount(prev => (prev + 1) % 4);
    }, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  // Rotate through texts
  useEffect(() => {
    if (texts.length <= 1) return;
    
    const textInterval = setInterval(() => {
      setCurrentText(prev => {
        const currentIndex = texts.indexOf(prev);
        return texts[(currentIndex + 1) % texts.length];
      });
    }, 2500);
    
    return () => clearInterval(textInterval);
  }, [texts]);
  
  // Simulate progress
  useEffect(() => {
    if (!showProgress) return;
    
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + Math.random() * 15;
        return newProgress >= 95 ? 95 : newProgress;
      });
    }, 800);
    
    return () => clearInterval(progressInterval);
  }, [showProgress]);
  
  return (
    <AILoadingContainer>
      <BrainContainer>
        <BrainCore>
          <BrainInner />
        </BrainCore>
        <NeuralNetwork>
          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
            <NeuralNode
              key={index}
              $delay={index * 0.3}
              $duration={3 + (index % 2)}
              $distance={60 + (index * 8)}
            />
          ))}
        </NeuralNetwork>
      </BrainContainer>
      
      <LoadingText>
        <LoadingTextWrapper>
          <LoadingTextContent>{currentText}</LoadingTextContent>
          <LoadingDots>{'.'.repeat(dotCount)}</LoadingDots>
        </LoadingTextWrapper>
      </LoadingText>
      
      <Subtitle>{subtitle}</Subtitle>
      
      {showProgress && (
        <ProgressBar>
          <ProgressFill $progress={progress} />
        </ProgressBar>
      )}
    </AILoadingContainer>
  );
};
